// Parses a hast `<table>` into `ChatTableData`. Returns `null` when
// the shape can't be safely exported (no thead, blank headers, merged
// cells, empty body, all-blank rows) so callers fall back to plain HTML.
import type { Element as HastElement, ElementContent } from "hast";

import { parseLink } from "../markdown-actions";

export type ColumnType =
    | "currency"
    | "date"
    | "status"
    | "boolean"
    | "row-counter"
    | "source"
    | "text";

export interface HeaderSpec {
    text: string;
    columnType?: ColumnType;
}

export type CellTag =
    | { kind: "status"; variant: string }
    | { kind: "bool"; value: boolean };

export interface CellSourceRef {
    // Legacy `link://source` cells carry a document id (+ optional page);
    // native `cite://cN` cells carry an opaque handle instead, resolved to a
    // document/page/bbox at render time. Exactly one of the two is set.
    document_id?: number;
    page?: number;
    handle?: string;
    // Literal rendered text of the anchor (e.g. `"[7]"`). The renderer
    // uses this to decide pure-source vs. mixed-content routing.
    linkLabel?: string;
}

export interface CellSpec {
    /** Whitespace-collapsed display text. */
    text: string;
    /** Pre-normalization text run preserved for sort tie-breakers. */
    rawText: string;
    tag?: CellTag;
    /** Every source citation in the cell, in document order. */
    sources?: CellSourceRef[];
}

export interface ChatTableData {
    title?: string;
    headers: HeaderSpec[];
    rows: CellSpec[][];
}

const HEADING_TAGS = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);

/** `<br>` renders as a single space so multi-line cells don't collapse
 * into a single run of letters. */
function rawTextContent(node: ElementContent): string {
    if (node.type === "text") return node.value;
    if (node.type !== "element") return "";
    if (node.tagName === "br") return " ";
    return (node.children ?? []).map(rawTextContent).join("");
}

function normalize(text: string): string {
    return text.trim().replace(/\s+/g, " ");
}

function spanValue(el: HastElement, key: "colSpan" | "rowSpan"): number {
    const raw = el.properties?.[key];
    if (typeof raw === "number") return raw;
    if (typeof raw === "string") {
        const n = Number(raw);
        return Number.isFinite(n) ? n : 1;
    }
    return 1;
}

function hasMergedSpan(el: HastElement): boolean {
    return spanValue(el, "colSpan") > 1 || spanValue(el, "rowSpan") > 1;
}

function findFirstElement<T>(
    children: ReadonlyArray<ElementContent>,
    pick: (el: HastElement) => T | undefined
): T | undefined {
    for (const node of children) {
        if (node.type !== "element") continue;
        const hit = pick(node);
        if (hit !== undefined) return hit;
        const childHit = findFirstElement(node.children ?? [], pick);
        if (childHit !== undefined) return childHit;
    }
    return undefined;
}

function elementChildren(parent: HastElement, tagName: string): HastElement[] {
    const out: HastElement[] = [];
    for (const child of parent.children ?? []) {
        if (child.type === "element" && child.tagName === tagName) {
            out.push(child);
        }
    }
    return out;
}

function extractTag(cell: HastElement): CellTag | undefined {
    return findFirstElement(cell.children ?? [], (el) => {
        if (el.tagName !== "span") return undefined;
        const status = el.properties?.dataStatus;
        if (typeof status === "string" && status.length > 0) {
            return { kind: "status", variant: status } as const;
        }
        const bool = el.properties?.dataBool;
        if (typeof bool === "string" && bool.length > 0) {
            return { kind: "bool", value: bool === "true" } as const;
        }
        return undefined;
    });
}

function nonEmptyString(value: unknown): string | null {
    return typeof value === "string" && value.length > 0 ? value : null;
}

function sourceRefFor(el: HastElement): CellSourceRef | undefined {
    const candidate =
        nonEmptyString(el.properties?.dataCustomHref) ??
        nonEmptyString(el.properties?.href);
    if (!candidate) return undefined;

    const parsed = parseLink(candidate);
    if (!parsed || parsed.action !== "source") return undefined;

    const linkLabel = normalize(rawTextContent(el)) || undefined;

    // cite://cN carries an opaque handle (not a doc id/page); the chip resolves
    // it at render time from the per-message resolution map.
    if (parsed.protocol === "cite") {
        const ref: CellSourceRef = { handle: parsed.target };
        if (linkLabel) ref.linkLabel = linkLabel;
        return ref;
    }

    const documentId = parseInt(parsed.target, 10);
    if (!Number.isFinite(documentId) || documentId <= 0) return undefined;

    const ref: CellSourceRef = { document_id: documentId };
    if (linkLabel) ref.linkLabel = linkLabel;

    const pageRaw = parsed.params?.page;
    if (pageRaw) {
        const page = parseInt(pageRaw, 10);
        if (Number.isFinite(page) && page > 0) ref.page = page;
    }
    return ref;
}

// Collects every source citation in document order. A matched element is
// not descended into: the rewritten-Button shape mirrors the href onto
// the wrapper, so recursing would double-count its inner anchor.
function collectSources(
    children: ReadonlyArray<ElementContent>,
    out: CellSourceRef[]
): void {
    for (const node of children) {
        if (node.type !== "element") continue;
        const ref = sourceRefFor(node);
        if (ref) {
            out.push(ref);
            continue;
        }
        collectSources(node.children ?? [], out);
    }
}

function extractSources(cell: HastElement): CellSourceRef[] {
    const out: CellSourceRef[] = [];
    collectSources(cell.children ?? [], out);
    return out;
}

function parseCell(cell: HastElement): CellSpec {
    const raw = rawTextContent(cell);
    const text = normalize(raw);
    const out: CellSpec = { text, rawText: raw };
    const tag = extractTag(cell);
    if (tag) out.tag = tag;
    const sources = extractSources(cell);
    if (sources.length > 0) out.sources = sources;
    return out;
}

// Walks `siblings[0..before-1]` backward; the nearest <h1>-<h6> wins.
// `before` defaults to the array length so callers can either pass a
// pre-sliced array or pass the parent children + the table's own
// index without allocating. Returns `headingIndex` so the caller (the
// rehype plugin) can splice the heading out of the tree to prevent the
// title from rendering twice (once as a heading, once in the toolbar).
export function inferTitleFromSiblings(
    siblings: ReadonlyArray<ElementContent>,
    before: number = siblings.length
): { title: string; headingIndex: number } | undefined {
    for (let i = before - 1; i >= 0; i--) {
        const node = siblings[i];
        if (node.type !== "element") continue;
        if (!HEADING_TAGS.has(node.tagName)) continue;
        const text = normalize(rawTextContent(node));
        if (!text) return undefined;
        return { title: text, headingIndex: i };
    }
    return undefined;
}

export function parseMarkdownTable(node: HastElement): ChatTableData | null {
    if (node.type !== "element" || node.tagName !== "table") return null;

    const thead = elementChildren(node, "thead")[0];
    if (!thead) return null;

    const headerRow = elementChildren(thead, "tr")[0];
    if (!headerRow) return null;

    const headerCells = elementChildren(headerRow, "th");
    if (headerCells.length === 0) return null;
    if (headerCells.some(hasMergedSpan)) return null;

    const headers: HeaderSpec[] = headerCells.map((cell) => ({
        text: normalize(rawTextContent(cell)),
    }));
    if (headers.every((h) => h.text === "")) return null;

    const tbody = elementChildren(node, "tbody")[0];
    if (!tbody) return null;

    const bodyRows = elementChildren(tbody, "tr");
    if (bodyRows.length === 0) return null;

    const rows: CellSpec[][] = [];
    for (const tr of bodyRows) {
        const tds = elementChildren(tr, "td");
        if (tds.some(hasMergedSpan)) return null;
        const cells = tds.map(parseCell).slice(0, headers.length);
        while (cells.length < headers.length) {
            cells.push({ text: "", rawText: "" });
        }
        rows.push(cells);
    }

    if (rows.every((row) => row.every((cell) => cell.text === ""))) {
        return null;
    }

    return { headers, rows };
}
